/**
 * Highlights (metric 25): AI Insight Dashboard — sidebar topics + segment chips +
 * birleşik kesit/tema kartları + yapılandırılmış AI kutusu. SCSS module (Tailwind yok).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal, flushSync } from "react-dom";
import { Drawer, message, Spin, Tag } from "antd";
import {
  RightOutlined,
  DownOutlined,
  StopOutlined,
  AppstoreOutlined,
  CoffeeOutlined,
  HomeOutlined,
  SmileOutlined,
  ThunderboltOutlined,
  StarFilled,
} from "@ant-design/icons";
import { getIntlLocale } from "util/locale";
import { useTranslation } from "react-i18next";
import WelcomeDrawerReviewsList from "pages/Welcome/WelcomeDrawerReviewsList";
import { useWelcomeReviewsDrawerCloseEffect } from "pages/Welcome/useWelcomeReviewsDrawerCloseEffect";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import { fetchWelcomeWidgetMetricReviews } from "queries/welcomeWidgetMetricReviews";
import {
  formatWelcomeReviewScopeLabel,
  isWelcomeHierarchicalTopicTab,
  parseWelcomeFiniteCount,
  resolveWelcomeDisplayReviewCount,
  resolveWelcomeReviewScopeFromData,
} from "pages/Welcome/welcomeReviewScopeLabel";
import {
  parseHighlightsNarrative,
  parseInsightsFromWidgetItems,
  normalizeHighlightsWidgetItems,
} from "./welcomeHighlightsStructured";
import { collectGenaiItemReviewIds } from "./welcomeGenaiReviewIds";
import dashStyles from "./welcomeHighlightsDashboard.module.scss";

function decodeEnt(text) {
  if (text == null) return text;
  const s = String(text);
  return s
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

const TOPIC_ICON_COMPONENTS = [
  CoffeeOutlined,
  HomeOutlined,
  AppstoreOutlined,
  SmileOutlined,
  ThunderboltOutlined,
  StarFilled,
];

function normalizeMatchKey(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** "Özet 1", "Summary 2" gibi LLM yer tutucu başlıklar — gerçek tema/kesit satırını öne çıkar. */
function isGenericPlaceholderInsightTitle(s) {
  const t = normalizeMatchKey(String(s ?? "").trim());
  if (!t) return true;
  if (/^(özet|summary|insight|bulgu|finding)\s*\d+$/.test(t)) return true;
  if (/^\d+\s*(özet|summary|insight|bulgu|finding)(\s+\d+)?$/.test(t)) return true;
  return false;
}

function parseReviewCountFromTabContent(tab) {
  const c = String(tab?.content ?? "").trim();
  if (!c.startsWith("{") && !c.startsWith("[")) return null;
  try {
    const p = JSON.parse(c);
    const top =
      p?.summary_stats?.review_count ??
      p?.summary_stats?.total_reviews ??
      p?.review_count;
    if (top != null && top !== "" && Number.isFinite(Number(top))) {
      return Math.round(Number(top));
    }
    // Do not sum items[].review_count — those are per-highlight evidence counts.
  } catch {
    /* ignore */
  }
  return null;
}

function sumReviewCountFromItems(items) {
  const list = Array.isArray(items) ? items : [];
  let sum = 0;
  let any = false;
  for (const it of list) {
    const n =
      it?.review_count ??
      it?.reviewCount ??
      it?.num_reviews ??
      it?.reviews_count;
    if (n != null && n !== "" && Number.isFinite(Number(n))) {
      sum += Number(n);
      any = true;
    }
  }
  return any ? Math.round(sum) : null;
}

function tryDescriptionJsonField(item, pick) {
  if (!item || typeof item !== "object") return null;
  const desc = item.description;
  if (typeof desc !== "string" || !desc.trim().startsWith("{")) return null;
  try {
    const o = JSON.parse(desc);
    if (o == null || typeof o !== "object") return null;
    return pick(o);
  } catch {
    return null;
  }
}

/** Theme label from API items[] (LLM `theme` or legacy aliases, or JSON in `description`). */
export function extractItemTheme(item) {
  if (!item || typeof item !== "object") return null;
  const v =
    item.theme ??
    item.theme_label ??
    item.theme_name ??
    item.tema ??
    item.sub_topic ??
    item.subtopic ??
    item.topic_theme;
  if (v != null && String(v).trim() !== "") return String(v).trim();
  const fromJson = tryDescriptionJsonField(
    item,
    (o) => o.theme ?? o.tema ?? o.theme_name ?? o.theme_label,
  );
  if (fromJson != null && String(fromJson).trim() !== "") return String(fromJson).trim();
  return null;
}

/** pivot_key:pivot_value (LLM çıktısı) — kart segment eşlemesi için; pivot_key ile widget known aynı olsa bile eşlem okur. */
function extractItemPivotPairKey(item) {
  if (!item || typeof item !== "object") return null;
  const pkRaw = item.pivot_key;
  const pk = pkRaw != null ? String(pkRaw).trim() : "";
  if (!pk) return null;
  const pvRaw = item.pivot_value;
  if (pvRaw == null || (typeof pvRaw === "string" && String(pvRaw).trim() === "")) {
    return null;
  }
  const canon =
    typeof pvRaw === "boolean"
      ? pvRaw ? "true" : "false"
      : typeof pvRaw === "number" && Number.isFinite(pvRaw)
        ? String(pvRaw)
        : String(pvRaw).trim();
  return `${pk}:${canon}`;
}

/**
 * Kart → segment filtresinde kullanılacak anahtar.
 * Öncelik: açık `segment`, pivot çifti, segment_display (okunabilir mikro kesit).
 */
export function extractItemSegment(item) {
  if (!item || typeof item !== "object") return null;
  const pair = extractItemPivotPairKey(item);
  const candidates = [
    item.segment,
    item.segment_label,
    item.segment_value,
    pair,
    item.segment_display,
    item.pivot_value,
    item.pivot,
    item.segment_id,
    item.segment_key,
    item.pivot_segment,
    item.audience_segment,
  ];
  for (const raw of candidates) {
    if (raw == null) continue;
    const s =
      typeof raw === "boolean" ? String(raw) : typeof raw === "number" ? String(raw) : String(raw).trim();
    if (s) return s;
  }
  return null;
}

/**
 * Yalnızca chip üretimi ve gruplama için: iki boyutlu mikro kesit çıkarımı (LLM kartları).
 */
export function deriveMicroSegmentKey(item) {
  if (!item || typeof item !== "object") return null;
  if (item.segment != null && String(item.segment).trim()) return String(item.segment).trim();
  const pk = extractItemPivotPairKey(item);
  if (pk) return pk;
  if (item.segment_display != null && String(item.segment_display).trim()) {
    return String(item.segment_display).trim();
  }
  return null;
}

function itemMatchesSegmentKeys(item, matchKeys) {
  const seg = extractItemSegment(item);
  if (seg == null) return false;
  const n = normalizeMatchKey(seg);
  return (matchKeys || []).some((k) => {
    const nk = normalizeMatchKey(k);
    if (!nk) return false;
    return n === nk || n.includes(nk) || nk.includes(n);
  });
}

function itemMatchesThemeKey(item, themeKey) {
  if (themeKey == null) return true;
  const th = extractItemTheme(item);
  if (th == null) return false;
  return normalizeMatchKey(th) === themeKey;
}

/** Stable id for highlight tag row (LLM `highlight_id` preferred). */
function getHighlightId(item, index) {
  if (!item || typeof item !== "object") return `h-${index}`;
  const h = item.highlight_id ?? item.id;
  if (h != null && String(h).trim() !== "") return String(h).trim();
  return `h-${index}`;
}

/** Short label for a highlight chip: tag_label → segment_display → non-generic title / tema / pivot. */
function getHighlightTagLabel(item, index) {
  if (!item || typeof item !== "object") return `— ${index + 1}`;
  const t = item.tag_label ?? item.segment_display;
  if (t != null && String(t).trim() !== "") return String(t).trim();
  const title = String(item.title ?? "").trim();
  if (title && !isGenericPlaceholderInsightTitle(title)) {
    return title.length > 52 ? `${title.slice(0, 49)}…` : title;
  }
  const headline = String(item.headline ?? "").trim();
  if (headline) return headline.length > 52 ? `${headline.slice(0, 49)}…` : headline;
  const th = extractItemTheme(item);
  if (th && !isThemeGenericLabel(th)) return String(th).trim();
  if (item.pivot_key != null && String(item.pivot_key).trim() && item.pivot_value != null) {
    return `${String(item.pivot_key)}: ${String(item.pivot_value)}`;
  }
  return `— ${index + 1}`;
}

function isThemeGenericLabel(themeStr) {
  const t = normalizeMatchKey(String(themeStr ?? "").trim());
  if (!t) return true;
  if (t === normalizeMatchKey("Genel")) return true;
  if (t === "general" || t === "all") return true;
  return false;
}

/**
 * Birleşik kart rozeti: LLM `card_kind` veya pivot+tema varlığından türetilir.
 * - theme: başlık öncelikle tema
 * - segment: başlık öncelikle kitle/kesit
 * - segment_theme: ikisi birden
 */
export function inferHighlightCardKind(item) {
  if (!item || typeof item !== "object") return "segment_theme";
  const raw = item.card_kind ?? item.cardKind ?? item.highlight_card_type;
  if (typeof raw === "string" && raw.trim()) {
    const s = raw.trim().toLowerCase();
    if (["segment", "seg", "kitle"].includes(s)) return "segment";
    if (["theme", "tema"].includes(s)) return "theme";
    if (["segment_theme", "segment-theme", "both", "ikisi", "combined"].includes(s)) {
      return "segment_theme";
    }
  }
  const hasPivot = Boolean(extractItemPivotPairKey(item));
  const th = extractItemTheme(item);
  const themeSpec = Boolean(th && !isThemeGenericLabel(th));
  if (hasPivot && themeSpec) return "segment_theme";
  if (hasPivot && !themeSpec) return "segment";
  if (!hasPivot && themeSpec) return "theme";
  return "segment_theme";
}

function unifiedCardPrimaryTitle(item, kind) {
  const rawTitle = decodeEnt(String(item.title ?? "").trim());
  const title = isGenericPlaceholderInsightTitle(rawTitle) ? "" : rawTitle;
  const tag = decodeEnt(String(item.tag_label ?? "").trim());
  const segLine = decodeEnt(String(item.segment_display ?? "").trim());
  const headline = decodeEnt(String(item.headline ?? "").trim());
  const th = extractItemTheme(item);
  const thTrim = th && !isThemeGenericLabel(th) ? String(th).trim() : "";
  if (kind === "theme") {
    if (thTrim) return thTrim;
    return title || headline || tag || segLine || "—";
  }
  if (kind === "segment") {
    if (segLine) return segLine;
    const pair = extractItemPivotPairKey(item);
    if (pair && item.pivot_key != null) {
      return decodeEnt(`${String(item.pivot_key).trim()}: ${String(item.pivot_value)}`);
    }
    return title || headline || tag || thTrim || "—";
  }
  return title || headline || tag || segLine || thTrim || "—";
}

function unifiedCardSecondaryLine(item, kind) {
  const desc = decodeEnt(String(item.description ?? "").trim());
  const segLine = decodeEnt(String(item.segment_display ?? "").trim());
  const th = extractItemTheme(item);
  const thOk = th && !isThemeGenericLabel(th);
  const pivotPair = extractItemPivotPairKey(item);
  if (kind === "theme") {
    if (segLine) return segLine;
    if (pivotPair) return decodeEnt(pivotPair);
    if (desc) return desc.length > 180 ? `${desc.slice(0, 177)}…` : desc;
    return "";
  }
  if (kind === "segment") {
    if (thOk) return String(th).trim();
    if (desc) return desc.length > 180 ? `${desc.slice(0, 177)}…` : desc;
    return "";
  }
  const parts = [
    thOk ? String(th).trim() : null,
    desc ? (desc.length > 140 ? `${desc.slice(0, 137)}…` : desc) : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

function extractItemScore(item) {
  if (!item || typeof item !== "object") return null;
  const n = item.score ?? item.theme_score ?? item.sentiment_score;
  if (n == null || n === "") return null;
  const num = Number(n);
  return Number.isFinite(num) ? num : null;
}

const THEME_EMOJI_POOL = ["🎯", "✨", "📌", "💡", "🔍", "📊", "🏷", "🌟"];

function emojiForThemeLabel(label) {
  const s = String(label || "");
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return THEME_EMOJI_POOL[Math.abs(h) % THEME_EMOJI_POOL.length];
}

/**
 * Items visible for current segment (same rules as buildCombinedTextForSegment).
 */
export function itemsForActiveSegment(rawItems, activeSegment) {
  const list = Array.isArray(rawItems) ? rawItems : [];
  if (!activeSegment || activeSegment.id === "all") return list;
  return list.filter((it) => itemMatchesSegmentKeys(it, activeSegment.matchKeys));
}

export function filterItemsBySegmentAndTheme(rawItems, activeSegment, themeKey) {
  return itemsForActiveSegment(rawItems, activeSegment).filter((it) =>
    itemMatchesThemeKey(it, themeKey),
  );
}

function buildCombinedTextFromItems(items) {
  const arr = Array.isArray(items) ? items : [];
  if (arr.length === 0) return "";
  return arr
    .map((m) => {
      const d = m.description ?? m.summary ?? "";
      if (typeof d === "string" && d.trim().startsWith("{")) return d.trim();
      if (typeof d === "string") return d.trim();
      try {
        return JSON.stringify(m);
      } catch {
        return "";
      }
    })
    .filter(Boolean)
    .join("\n\n");
}

/**
 * Unique theme cards for the current segment (from items that declare `theme`).
 */
export function buildThemeCardsFromItems(segmentScopedItems) {
  const items = Array.isArray(segmentScopedItems) ? segmentScopedItems : [];
  const map = new Map();
  for (const it of items) {
    const raw = extractItemTheme(it);
    if (!raw) continue;
    const nk = normalizeMatchKey(raw);
    if (!nk) continue;
    if (!map.has(nk)) {
      map.set(nk, {
        id: nk,
        label: raw.trim(),
        emoji: emojiForThemeLabel(raw),
        score: extractItemScore(it),
      });
    } else {
      const entry = map.get(nk);
      const s = extractItemScore(it);
      if (s != null && (entry.score == null || s > entry.score)) entry.score = s;
    }
  }
  return Array.from(map.values()).sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
  );
}

function itemHasInsightBlob(item) {
  if (!item || typeof item !== "object") return false;
  const d = item.description ?? item.summary ?? "";
  const ti = item.title ?? "";
  if (String(d).trim() || String(ti).trim()) return true;
  if (Array.isArray(item.evidence) && item.evidence.length) return true;
  if (item.meaning || item.action) return true;
  return false;
}

/** Pivot value → readable segment label */
function segmentLabelFromPivot(pivotKey, raw) {
  const k = pivotKey != null ? String(pivotKey).trim() : "";
  const v = raw == null ? "" : String(raw).trim();
  if (!v) return "—";
  if (/^adultcount$/i.test(k) || /^childcount$/i.test(k)) {
    return `${v} ${/adult/i.test(k) ? "yetişkin" : "çocuk"}`;
  }
  return v.replace(/_/g, " ");
}

/**
 * Segment chips: pivot values from widget, or demo ids when no pivot.
 * hasData: items[] içinde o segmente ait en az bir obje var mı.
 * hasInsight: eşleşen objede title/description/evidence/meaning/action var mı.
 */
export function buildHighlightsSegments(widget, items) {
  const rawItems = Array.isArray(items) ? items : [];
  const vals = Array.isArray(widget?.pivot_values) ? widget.pivot_values : [];
  const pk = widget?.pivot_key;

  // Kullanıcı widget’ta tek pivot değeri seçmiş olabilir (ör. tek vendor): widget satırından
  // fazla kesit çıkmıyorsa bile, llm kartlarından `segment` / pivot_key+pivot_value ile chip üretilir.
  if (vals.length === 1) {
    const micro = buildMicroSegmentsFromItems(rawItems);
    const allChip = {
      id: "all",
      labelKey: "highlightsDashSegmentAll",
      matchKeys: ["all", "aggregate", "tumu", "genel", String(vals[0])],
      rawValue: "all",
      hasData: true,
      hasInsight: rawItems.some((it) => itemHasInsightBlob(it)),
    };
    if (micro.length > 0) {
      return [allChip, ...micro];
    }
    return [allChip];
  }

  if (vals.length === 0) {
    const demo = [
      {
        id: "all",
        labelKey: "highlightsDashSegmentAll",
        matchKeys: ["all", "aggregate", "tumu", "genel"],
        rawValue: "all",
      },
      {
        id: "seg-demo-1",
        labelKey: "highlightsDashSegmentDemoFamily",
        matchKeys: ["family", "seg-demo-1", "aile", "FAMILY"],
        rawValue: "family",
      },
      {
        id: "seg-demo-2",
        labelKey: "highlightsDashSegmentDemoCouple",
        matchKeys: ["couple", "seg-demo-2", "cift", "couples"],
        rawValue: "couple",
      },
      {
        id: "seg-demo-3",
        labelKey: "highlightsDashSegmentDemoBusiness",
        matchKeys: ["business", "seg-demo-3", "is", "BUSINESS"],
        rawValue: "business",
      },
    ];
    return demo.map((d) => {
      if (d.id === "all") {
        return { ...d, hasData: true, hasInsight: true };
      }
      const hasItem = rawItems.some(
        (it) => itemMatchesSegmentKeys(it, d.matchKeys),
      );
      const hasInsight = rawItems.some(
        (it) =>
          itemMatchesSegmentKeys(it, d.matchKeys) && itemHasInsightBlob(it),
      );
      return {
        ...d,
        hasData: hasItem,
        hasInsight: hasInsight,
      };
    });
  }

  return [
    {
      id: "all",
      labelKey: "highlightsDashSegmentAll",
      matchKeys: ["all", "aggregate", "tumu", "genel"],
      rawValue: "all",
      hasData: true,
      hasInsight: true,
    },
    ...vals.map((v, i) => {
      const id = `seg-${i}-${String(v)}`;
      const label = segmentLabelFromPivot(pk, v);
      const matchKeys = [String(v), id, label];
      const hasItem = rawItems.some((it) =>
        itemMatchesSegmentKeys(it, matchKeys),
      );
      const hasInsight = rawItems.some(
        (it) =>
          itemMatchesSegmentKeys(it, matchKeys) && itemHasInsightBlob(it),
      );
      return {
        id,
        label,
        matchKeys,
        rawValue: String(v),
        hasData: hasItem,
        hasInsight: hasInsight,
      };
    }),
  ];
}

function splitActionNumbered(actionText) {
  const tx = String(actionText || "").trim();
  if (!tx) return [];
  const parts = tx
    .split(/\n+|(?=\d+[\).]\s)|(?<=[.!?])\s+(?=[A-ZÇĞİÖŞÜ])/u)
    .map((s) => s.replace(/^\d+[\).]\s*/, "").trim())
    .filter(Boolean);
  if (parts.length <= 1) return [tx];
  return parts;
}

/**
 * Summary tab `id` (örn. topic-4) ↔ worker `topic_tab_id` (örn. topic_12345) eşlemesi.
 * widget.topic_ids sırası, summary_tabs (topic-1, topic-2, …) ile aynı sırada olmalı.
 */
function itemTopicTabMatchesActiveTab(item, activeTab, widget) {
  if (!item || typeof item !== "object") return false;
  const want = String(activeTab?.id ?? "").trim();
  if (!want) return true;
  const itemTab = String(item.topic_tab_id ?? item.topic_id ?? "").trim();
  if (!itemTab) return false;
  if (itemTab === want) return true;
  const tids = Array.isArray(widget?.topic_ids)
    ? widget.topic_ids.map((x) => Number(x)).filter((n) => n > 0)
    : [];
  const m = want.match(/^topic-(\d+)$/i);
  if (m && tids.length) {
    const idx = parseInt(m[1], 10) - 1;
    if (idx >= 0 && idx < tids.length) {
      if (itemTab === `topic_${tids[idx]}`) return true;
    }
  }
  return false;
}

/** Resolve topic library id for matching `topic_ids` / worker `topic_tab_id` (heading uses name via topic_labels). */
function resolveHighlightsTopicLibId(tab, widget) {
  if (!tab || typeof tab !== "object") return null;
  const explicit =
    tab.topic_id ?? tab.topic_lib_id ?? tab.topicLibId;
  if (
    explicit != null &&
    Number.isFinite(Number(explicit)) &&
    Number(explicit) > 0
  ) {
    return Number(explicit);
  }
  const tabKey = String(tab.id ?? "").trim();
  const rawIds =
    widget?.topic_ids ??
    widget?.industry_topic_ids ??
    widget?.data?.topic_ids ??
    [];
  const tids = Array.isArray(rawIds)
    ? rawIds.map((x) => Number(x)).filter((n) => n > 0)
    : [];
  const lk = tabKey.toLowerCase();
  if (lk === "general" || lk === "global") return null;
  if (
    tids.length &&
    Number.isFinite(Number(tabKey)) &&
    Number(tabKey) > 0 &&
    tids.includes(Number(tabKey))
  ) {
    return Number(tabKey);
  }
  const m = tabKey.match(/^topic-(\d+)$/i);
  if (m && tids.length) {
    const idx = parseInt(m[1], 10) - 1;
    if (idx >= 0 && idx < tids.length) return tids[idx];
    const n = parseInt(m[1], 10);
    if (tids.includes(n)) return n;
  }
  return null;
}

function topicIdsArrayForHighlights(widget) {
  const raw =
    widget?.topic_ids ??
    widget?.industry_topic_ids ??
    widget?.data?.topic_ids ??
    [];
  return Array.isArray(raw)
    ? raw.map((x) => Number(x)).filter((n) => n > 0)
    : [];
}

function topicLabelsArrayForHighlights(widget, narrativeData) {
  const tids = topicIdsArrayForHighlights(widget);
  const raw =
    widget?.topic_labels ??
    widget?.data?.topic_labels ??
    narrativeData?.topic_labels ??
    [];
  if (!Array.isArray(raw)) {
    return tids.length ? tids.map(() => "") : [];
  }
  if (tids.length === 0) {
    return raw.map((l) =>
      l != null && String(l).trim() !== "" ? String(l).trim() : "",
    );
  }
  return tids.map((_, i) => {
    const l = raw[i];
    return l != null && String(l).trim() !== "" ? String(l).trim() : "";
  });
}

/** Sidebar + report title: topic display name from `topic_labels` (same order as topic_ids). */
function highlightsTabHeadingLabel(tab, widget, labelsAligned) {
  const tabKey = String(tab?.id ?? "").trim();
  const lk = tabKey.toLowerCase();
  if (lk === "general" || lk === "global") {
    const raw = String(tab?.label ?? "").trim();
    return raw ? decodeEnt(raw) : null;
  }

  const tids = topicIdsArrayForHighlights(widget);
  const labels = Array.isArray(labelsAligned) ? labelsAligned : [];
  const tid = resolveHighlightsTopicLibId(tab, widget);

  let idx = -1;
  if (tid != null && tids.length) idx = tids.indexOf(tid);
  if (idx < 0) {
    const m = tabKey.match(/^topic-(\d+)$/i);
    if (m) {
      const i = parseInt(m[1], 10) - 1;
      if (i >= 0 && (tids.length === 0 || i < tids.length)) idx = i;
    }
  }

  if (idx >= 0 && idx < labels.length) {
    const lab = String(labels[idx] ?? "").trim();
    if (lab) return decodeEnt(lab);
  }

  const tabFallback = decodeEnt(String(tab?.label ?? "").trim());
  if (tabFallback) return tabFallback;

  return null;
}

/** POST /welcome/widget/metric-25/reviews — DB topic_tab_id (topic_<libId>). */
function highlightTopicTabIdForApi(activeTopicKey, widget) {
  const want = String(activeTopicKey ?? "").trim();
  const rawIds =
    widget?.topic_ids ??
    widget?.industry_topic_ids ??
    widget?.data?.topic_ids ??
    [];
  const tids = Array.isArray(rawIds)
    ? rawIds.map((x) => Number(x)).filter((n) => n > 0)
    : [];
  const m = want.match(/^topic-(\d+)$/i);
  if (m && tids.length) {
    const idx = parseInt(m[1], 10) - 1;
    if (idx >= 0 && idx < tids.length) return `topic_${tids[idx]}`;
  }
  return want;
}

function widgetScopePivotLine(widget) {
  const k = widget?.pivot_key != null ? String(widget.pivot_key).trim() : "";
  if (!k) return "";
  const raw = widget?.pivot_values;
  const vals = Array.isArray(raw)
    ? raw.filter((v) => v != null && String(v).trim() !== "")
    : [];
  if (vals.length === 0) return k;
  if (vals.length === 1) return `${k}: ${String(vals[0])}`;
  return `${k}: ${vals.map((v) => String(v)).join(", ")}`;
}

/**
 * Highlights POST `segment`, worker HighlightsSlice.segment_key ile aynı sıra önceliği —
 * özellikle known pivot≠kart pivot olduğunda (örn. vendor known, kart bookingNumberOfNights).
 */
export function highlightSliceSegmentLabelForReviewsApi(widget, item) {
  if (!item || typeof item !== "object") return null;
  const rs = item.segment;
  if (rs != null && String(rs).trim() !== "") return String(rs).trim();
  const pair = extractItemPivotPairKey(item);
  if (pair) return pair;
  const wpk = widget?.pivot_key != null ? String(widget.pivot_key).trim() : "";
  const ipk = item.pivot_key != null ? String(item.pivot_key).trim() : "";
  const ipv = item.pivot_value;
  if (
    wpk &&
    ipk &&
    normalizeMatchKey(ipk) === normalizeMatchKey(wpk) &&
    ipv != null &&
    String(ipv).trim() !== ""
  ) {
    if (typeof ipv === "boolean") return ipv ? "true" : "false";
    if (typeof ipv === "number" && Number.isFinite(ipv)) return String(ipv);
    const s = String(ipv).trim();
    return s || null;
  }
  return null;
}

function slugMicroSegmentChipId(label, idx) {
  const s = String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 44);
  return s || `g${idx}`;
}

function uniqNonEmpty(strings) {
  const out = [];
  const seen = new Set();
  for (const raw of strings) {
    const s =
      raw == null
        ? ""
        : typeof raw === "boolean"
          ? String(raw)
          : typeof raw === "number"
            ? String(raw)
            : String(raw).trim();
    if (!s) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

/**
 * Widget pivot tek değerliyken bile, items[] içindeki segment / pivot çiftinden segment chip listesi.
 * Tümü + anlamlı alt dilimler (en az 2 grup veya grup + grupsuz kart karışımı) olunca döner.
 */
function buildMicroSegmentsFromItems(rawItems) {
  const list = Array.isArray(rawItems) ? rawItems : [];
  const buckets = new Map();

  for (const it of list) {
    const rawKey = deriveMicroSegmentKey(it);
    if (!rawKey) continue;
    const nk = normalizeMatchKey(rawKey);
    if (!nk) continue;
    let b = buckets.get(nk);
    if (!b) {
      b = { norm: nk, rawKey: String(rawKey).trim(), labels: new Set(), items: [] };
      buckets.set(nk, b);
    }
    b.items.push(it);
    const display =
      (it.segment_display && String(it.segment_display).trim()) ||
      (it.segment && String(it.segment).trim()) ||
      extractItemPivotPairKey(it) ||
      String(rawKey).trim();
    const d = decodeEnt(String(display).trim());
    if (d) b.labels.add(d);
  }

  if (buckets.size === 0) return [];

  let assigned = 0;
  for (const b of buckets.values()) assigned += b.items.length;
  const orphans = list.length - assigned;

  if (buckets.size < 2 && orphans === 0) return [];

  const rows = [];
  let idx = 0;
  for (const b of buckets.values()) {
    const labelCandidates = [...b.labels].filter(Boolean);
    labelCandidates.sort((a, c) => a.length - c.length || a.localeCompare(c, "tr", { sensitivity: "base" }));
    const label = labelCandidates[0] || b.rawKey;
    const matchKeys = uniqNonEmpty([
      b.norm,
      b.rawKey,
      ...b.labels,
      ...b.items.flatMap((it) => {
        const out = [];
        const k = deriveMicroSegmentKey(it);
        if (k) out.push(k);
        const seg = extractItemSegment(it);
        if (seg) out.push(seg);
        return out;
      }),
    ]);
    const hasInsight = b.items.some((it) => itemHasInsightBlob(it));
    rows.push({
      id: `seg-micro-${slugMicroSegmentChipId(label, idx)}-${idx}`,
      label,
      matchKeys,
      rawValue: b.rawKey,
      hasData: true,
      hasInsight,
    });
    idx += 1;
  }
  rows.sort((a, c) =>
    String(a.label).localeCompare(String(c.label), "tr", { sensitivity: "base" }),
  );
  return rows;
}

/** Pivot satırı: önce `pivot_key` + tek `pivot_value`; yoksa `description` JSON. */
function itemScopePivotLine(item) {
  if (!item || typeof item !== "object") return "";
  const k = item.pivot_key != null ? String(item.pivot_key).trim() : "";
  const pv = item.pivot_value;
  if (k) {
    if (pv == null) return k;
    const s = String(pv).trim();
    if (s === "") return k;
    return `${k}: ${s}`;
  }
  const fromJson = tryDescriptionJsonField(item, (o) => {
    const pk = o.pivot_key != null ? String(o.pivot_key).trim() : "";
    if (!pk) return null;
    const pvv = o.pivot_value;
    if (pvv == null) return pk;
    const ss = String(pvv).trim();
    if (ss === "") return pk;
    return `${pk}: ${pvv}`;
  });
  return fromJson && String(fromJson).trim() ? String(fromJson).trim() : "";
}

/**
 * Aynı anda birden çok highlight varken, ekrandaki `insights[0]` ile aynı `title`'a
 * sahip `items[]` satırını seç (aksi halde sadece `scopeItems[0]` kullanılır, tema/ pivot yanlış satırdan okunur).
 */
function findContextScopeItem({
  activeHighlightId,
  effectiveSingleHighlightItems,
  scopeItems,
  parsed,
}) {
  if (activeHighlightId != null && effectiveSingleHighlightItems?.[0]) {
    return effectiveSingleHighlightItems[0];
  }
  if (!Array.isArray(scopeItems) || scopeItems.length === 0) return null;
  if (scopeItems.length === 1) return scopeItems[0];
  if (parsed?.mode === "structured" && Array.isArray(parsed.insights) && parsed.insights[0]) {
    const want0 = String(parsed.insights[0].title || "").trim();
    if (want0) {
      const stripNum = (s) => s.replace(/^\d+\)\s*/, "").trim();
      const wS = stripNum(want0);
      for (const it of scopeItems) {
        const tit = String(it.title || "").trim();
        if (tit === want0) return it;
        if (stripNum(tit) === wS) return it;
      }
      for (const it of scopeItems) {
        const tit = String(it.title || "").trim();
        const tS = stripNum(tit);
        if (wS && tS && (tS === wS || tS.includes(wS) || wS.includes(tS))) {
          return it;
        }
      }
    }
  }
  return scopeItems[0];
}

/** Dynamo Reviews.date_gpid — ignore LLM numeric indices masquerading as ids. */
export function isPlausibleHighlightsReviewGpid(value) {
  const s = String(value ?? "").trim();
  if (!s) return false;
  if (/^\d+$/.test(s)) return false;
  return /^20\d{6}_[A-Za-z0-9_-]+$/.test(s);
}

/**
 * Metric-25 explicit review ids: Dynamo `date_gpid` from LLM item fields.
 */
function collectHighlightsReviewIdsFromItem(item) {
  if (!item || typeof item !== "object") return [];
  return collectGenaiItemReviewIds(item, 80).filter(isPlausibleHighlightsReviewGpid);
}

/** Per-highlight review count from explicit associated_gpids only. */
export function extractItemReviewCount(item) {
  if (!item || typeof item !== "object") return null;
  const ids = collectHighlightsReviewIdsFromItem(item);
  return ids.length > 0 ? ids.length : null;
}

function resolveHighlightsThemeKeysForReviewsApi(
  activeThemeLabel,
  contextSourceItem,
  { highlightScoped = false } = {},
) {
  const keys = [];
  const add = (raw) => {
    if (raw == null || String(raw).trim() === "") return;
    const k = normalizeMatchKey(String(raw).trim());
    if (k && !keys.includes(k)) keys.push(k);
  };

  if (highlightScoped && contextSourceItem) {
    add(contextSourceItem.tag_label);
    add(contextSourceItem.segment_display);
    const th = extractItemTheme(contextSourceItem);
    if (th && !isThemeGenericLabel(th)) add(th);
    return keys;
  }

  if (activeThemeLabel) {
    add(activeThemeLabel);
    return keys;
  }

  if (contextSourceItem) {
    add(contextSourceItem.tag_label);
    add(contextSourceItem.segment_display);
    const th = extractItemTheme(contextSourceItem);
    if (th && !isThemeGenericLabel(th)) add(th);
  }
  return keys;
}

function resolveHighlightsThemeForReviewsApi(
  activeThemeLabel,
  contextSourceItem,
  opts = {},
) {
  const keys = resolveHighlightsThemeKeysForReviewsApi(
    activeThemeLabel,
    contextSourceItem,
    opts,
  );
  return keys[0] || "";
}

/** Build metric-25 reviews POST body; scopes slice by selected highlight when possible. */
export function buildHighlightsReviewsRequestPayload({
  widgetId,
  widget,
  activeTopicKey,
  activeThemeLabel,
  contextSourceItem,
  highlightScoped = false,
}) {
  const payload = {
    widget_id: Number(widgetId),
    topic_tab_id: highlightTopicTabIdForApi(activeTopicKey, widget),
  };
  const explicitIds = collectHighlightsReviewIdsFromItem(contextSourceItem);
  if (explicitIds.length > 0) {
    payload.review_ids = explicitIds;
    return payload;
  }

  const sliceIdRaw =
    contextSourceItem?.slice_id ?? contextSourceItem?.sliceId ?? null;
  if (sliceIdRaw != null && Number.isFinite(Number(sliceIdRaw))) {
    payload.slice_id = Number(sliceIdRaw);
    return payload;
  }

  let segmentForApi = "all";
  const alignedSeg = contextSourceItem
    ? highlightSliceSegmentLabelForReviewsApi(widget, contextSourceItem)
    : null;
  if (alignedSeg && String(alignedSeg).trim() !== "") {
    segmentForApi = String(alignedSeg).trim().toLowerCase();
  }

  payload.segment = segmentForApi;
  const themeKeys = resolveHighlightsThemeKeysForReviewsApi(
    activeThemeLabel,
    contextSourceItem,
    { highlightScoped },
  );
  payload.theme = themeKeys[0] || "";
  if (themeKeys.length > 1) {
    payload.theme_keys = themeKeys.slice(1);
  }
  return payload;
}

/** Çoklu insight eşlemesi: parse çıktısındaki `title` → `items[]` satırı. */
function findItemForInsightTitle(scopeItems, insightTitle) {
  if (!Array.isArray(scopeItems) || scopeItems.length === 0) return null;
  const s = String(insightTitle || "").trim();
  if (!s) return scopeItems[0] ?? null;
  const stripNum = (x) => String(x || "").replace(/^\d+\)\s*/, "").trim();
  const a = stripNum(s);
  for (const it of scopeItems) {
    const t = String(it.title || "").trim();
    if (t === s) return it;
    if (stripNum(t) === a) return it;
  }
  for (const it of scopeItems) {
    const t = String(it.title || "").trim();
    const b = stripNum(t);
    if (a && b && (a === b || a.includes(b) || b.includes(a))) return it;
  }
  return scopeItems[0] ?? null;
}

/**
 * @param {object} [opts]
 * @param {boolean} [opts.withWidgetLine=true] – çoklu blokta yalnızca ilkte widget pivotu.
 */
function buildItemContextTagFields(item, widget, opts = { withWidgetLine: true }) {
  const wLine = opts.withWidgetLine !== false ? widgetScopePivotLine(widget) : "";
  const itLine = item ? itemScopePivotLine(item) : "";
  const th = item ? extractItemTheme(item) : null;
  const cardL = item
    ? String(item.tag_label ?? item.segment_display ?? "").trim()
    : "";
  const showItemPivotUnset =
    !!item &&
    item.pivot_key == null &&
    (item.pivot_value == null || String(item.pivot_value).trim() === "");
  const showCardL = Boolean(cardL && (!th || cardL !== th));
  const showItLine = Boolean(
    itLine && String(itLine).trim() && itLine !== wLine,
  );
  if (!wLine && !th && !showCardL && !showItemPivotUnset && !showItLine) return null;
  return { wLine, th, showCardL, cardL, showItemPivotUnset, itLine, showItLine };
}

function buildCombinedTextForSegment(activeSegment, bodyText, rawItems) {
  if (!activeSegment || activeSegment.id === "all") {
    return bodyText;
  }
  const matched = rawItems.filter((it) =>
    itemMatchesSegmentKeys(it, activeSegment.matchKeys),
  );
  if (matched.length === 0) return "";
  return matched
    .map((m) => {
      const d = m.description ?? m.summary ?? "";
      if (typeof d === "string" && d.trim().startsWith("{")) return d.trim();
      if (typeof d === "string") return d.trim();
      try {
        return JSON.stringify(m);
      } catch {
        return "";
      }
    })
    .filter(Boolean)
    .join("\n\n");
}

/**
 * @param {object} props
 * @param {Array} props.tabs - summary_tabs rows (id, label, content, review_count, …)
 * @param {object} [props.narrativeData] — widget.data ile aynı API satırı; `items` yedek kaynak.
 * @param {object} props.widget
 * @param {string[]} [props.topicLabelsAligned] — topic_ids ile aynı uzunlukta, indeks hizalı etiketler (25)
 */
export default function WelcomeHighlightsInsightDashboard({
  tabs,
  widget,
  narrativeData,
  topicLabelsAligned,
}) {
  const { t, i18n } = useTranslation();
  const openReviewsDrawerInFlightRef = useRef(false);
  const [reviewsDrawerOpen, setReviewsDrawerOpen] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsDrawerTitle, setReviewsDrawerTitle] = useState("");
  const [reviewsList, setReviewsList] = useState([]);

  const handleReviewsDrawerClose = useCallback(() => {
    setReviewsDrawerOpen(false);
    setReviewsLoading(false);
    setReviewsList([]);
    setReviewsDrawerTitle("");
  }, []);

  useWelcomeReviewsDrawerCloseEffect(reviewsDrawerOpen, handleReviewsDrawerClose);

  const safeTabs = Array.isArray(tabs) ? tabs : [];
  const rawItems = useMemo(() => {
    const sources = [
      Array.isArray(widget?.data?.items) ? widget.data.items : null,
      Array.isArray(narrativeData?.items) ? narrativeData.items : null,
      Array.isArray(narrativeData?.data?.items) ? narrativeData.data.items : null,
    ];
    for (const s of sources) {
      if (s && s.length) return s;
    }
    // Fallback: API may not expose items directly (older workers / cached responses).
    // Parse items embedded as JSON in each tab's content field and merge them so
    // pivot / theme / tag_label chips and filter bars render correctly.
    const tabParsed = [];
    for (const tab of safeTabs) {
      const c = String(tab?.content ?? "").trim();
      if (!c.startsWith("{") && !c.startsWith("[")) continue;
      try {
        const p = JSON.parse(c);
        const arr = Array.isArray(p) ? p : Array.isArray(p?.items) ? p.items : null;
        if (!arr) continue;
        for (const it of normalizeHighlightsWidgetItems(arr)) {
          if (it && typeof it === "object") tabParsed.push(it);
        }
      } catch {
        // malformed content — skip
      }
    }
    return tabParsed;
  }, [widget, narrativeData, safeTabs]);
  const widgetId = widget?.id ?? widget?.widget_id ?? widget?._id ?? null;

  const [activeTopicKey, setActiveTopicKey] = useState(
    () => String(safeTabs[0]?.id ?? "topic-1"),
  );
  const [activeSegmentId, setActiveSegmentId] = useState("all");
  const [activeThemeKey, setActiveThemeKey] = useState(null);
  /** null = tümü (kapsam içi birleşik özet); aksi halde scope’taki bir highlight kartı. */
  const [activeHighlightId, setActiveHighlightId] = useState(null);
  /** Slice-linked review count for selected highlight card (undefined = not resolved yet). */
  const [sliceReviewCount, setSliceReviewCount] = useState(undefined);

  const highlightLabelsResolved = useMemo(() => {
    if (Array.isArray(topicLabelsAligned) && topicLabelsAligned.length > 0) {
      return topicLabelsAligned;
    }
    return topicLabelsArrayForHighlights(widget, narrativeData);
  }, [topicLabelsAligned, widget, narrativeData]);

  const activeTab = useMemo(
    () => safeTabs.find((x) => String(x?.id) === String(activeTopicKey)) || safeTabs[0],
    [safeTabs, activeTopicKey],
  );

  const activeTabHeadingLabel = useMemo(
    () => highlightsTabHeadingLabel(activeTab, widget, highlightLabelsResolved),
    [activeTab, widget, highlightLabelsResolved],
  );

  const topicScopedItems = useMemo(() => {
    const list = Array.isArray(rawItems) ? rawItems : [];
    const tabId = String(activeTab?.id ?? "").trim();
    if (!tabId) return list;
    const scoped = list.filter((it) => itemTopicTabMatchesActiveTab(it, activeTab, widget));
    return scoped.length > 0 ? scoped : list;
  }, [rawItems, activeTab, widget]);

  const segments = useMemo(
    () => buildHighlightsSegments(widget, topicScopedItems),
    [widget, topicScopedItems],
  );

  /** Tek pivot değerinde sadece "Tümü" kaldı; ayrı kitle satırı gereksiz tekrar. */
  const showAudienceSegmentFilter = segments.length > 1;

  useEffect(() => {
    const cur = segments.find((s) => s.id === activeSegmentId);
    if (cur && cur.hasData !== false) return;
    const firstOk = segments.find((s) => s.hasData !== false);
    if (firstOk) setActiveSegmentId(firstOk.id);
  }, [segments]);

  const activeSegment = useMemo(
    () => segments.find((s) => s.id === activeSegmentId) || segments[0],
    [segments, activeSegmentId],
  );

  const segmentScopedItems = useMemo(
    () => itemsForActiveSegment(topicScopedItems, activeSegment),
    [topicScopedItems, activeSegment],
  );

  /** Bazı satırlarda `theme` var, bazılarında yok — “Tüm temalar” görünümünde uyarı gösterilir. */
  const mixedThemeCoverage = useMemo(() => {
    const items = Array.isArray(segmentScopedItems) ? segmentScopedItems : [];
    if (items.length === 0) return false;
    let withTheme = 0;
    let withoutTheme = 0;
    for (const it of items) {
      if (extractItemTheme(it)) withTheme += 1;
      else withoutTheme += 1;
    }
    return withTheme > 0 && withoutTheme > 0;
  }, [segmentScopedItems]);

  const hasThemeInItems = useMemo(
    () => segmentScopedItems.some((it) => extractItemTheme(it)),
    [segmentScopedItems],
  );

  const dynamicThemeCards = useMemo(
    () => buildThemeCardsFromItems(segmentScopedItems),
    [segmentScopedItems],
  );

  useEffect(() => {
    if (activeThemeKey == null) return;
    const ok = dynamicThemeCards.some((c) => c.id === activeThemeKey);
    if (!ok) setActiveThemeKey(null);
  }, [dynamicThemeCards, activeThemeKey]);

  const bodyText = decodeEnt(String(activeTab?.content ?? ""));
  const hasBody = bodyText.trim().length > 0;

  const isThemeFiltered = activeThemeKey != null;

  const sliceItemsForTheme = useMemo(
    () =>
      isThemeFiltered
        ? filterItemsBySegmentAndTheme(topicScopedItems, activeSegment, activeThemeKey)
        : null,
    [topicScopedItems, activeSegment, activeThemeKey, isThemeFiltered],
  );

  /** Mevcut segment + (varsa) tema daraltmasından sonraki kart havuzu. */
  const scopeItems = useMemo(() => {
    if (isThemeFiltered) {
      return Array.isArray(sliceItemsForTheme) ? sliceItemsForTheme : [];
    }
    return segmentScopedItems;
  }, [isThemeFiltered, sliceItemsForTheme, segmentScopedItems]);

  useEffect(() => {
    setActiveHighlightId(null);
  }, [activeTopicKey, activeSegmentId, activeThemeKey]);

  const effectiveSingleHighlightItems = useMemo(() => {
    if (activeHighlightId == null) return null;
    const one = scopeItems.find((it, idx) => getHighlightId(it, idx) === activeHighlightId);
    return one ? [one] : null;
  }, [activeHighlightId, scopeItems]);

  const segmentTextForParse = useMemo(() => {
    if (isThemeFiltered) {
      return buildCombinedTextFromItems(sliceItemsForTheme || []);
    }
    return buildCombinedTextForSegment(activeSegment, bodyText, topicScopedItems);
  }, [
    isThemeFiltered,
    sliceItemsForTheme,
    activeSegment,
    bodyText,
    topicScopedItems,
  ]);

  const parsed = useMemo(() => {
    if (effectiveSingleHighlightItems && effectiveSingleHighlightItems.length === 1) {
      return (
        parseInsightsFromWidgetItems(effectiveSingleHighlightItems) ??
        parseHighlightsNarrative(
          buildCombinedTextFromItems(effectiveSingleHighlightItems),
        )
      );
    }
    if (isThemeFiltered) {
      const items = sliceItemsForTheme || [];
      return (
        parseInsightsFromWidgetItems(items) ??
        parseHighlightsNarrative(buildCombinedTextFromItems(items))
      );
    }
    if (Array.isArray(segmentScopedItems) && segmentScopedItems.length > 0) {
      const fromItems =
        parseInsightsFromWidgetItems(segmentScopedItems) ??
        parseHighlightsNarrative(buildCombinedTextFromItems(segmentScopedItems));
      if (
        fromItems?.mode === "structured" &&
        Array.isArray(fromItems.insights) &&
        fromItems.insights.length > 0
      ) {
        return fromItems;
      }
      if (fromItems?.mode === "plain" && String(fromItems.text || "").trim()) {
        return fromItems;
      }
    }
    return parseHighlightsNarrative(segmentTextForParse);
  }, [
    effectiveSingleHighlightItems,
    isThemeFiltered,
    sliceItemsForTheme,
    segmentTextForParse,
    segmentScopedItems,
  ]);

  const activeThemeLabel = useMemo(() => {
    if (activeThemeKey == null) return null;
    const card = dynamicThemeCards.find((c) => c.id === activeThemeKey);
    return card?.label ?? activeThemeKey;
  }, [dynamicThemeCards, activeThemeKey]);

  const activeSegmentRaw = useMemo(() => {
    if (!activeSegment) return "all";
    if (activeSegment.id === "all") return "all";
    return activeSegment.rawValue ?? activeSegment.label ?? activeSegment.id;
  }, [activeSegment]);

  const insights =
    parsed.mode === "structured" && Array.isArray(parsed.insights)
      ? parsed.insights
      : [];

  /** Birden fazla highlight varsa seçim soldaki konu satırı altında; sağda uzun scroll yığını yok. */
  const useSidebarHighlightNav = scopeItems.length > 1;
  const needsHighlightPick = useSidebarHighlightNav && activeHighlightId == null;

  const showMultiInsightStack =
    activeHighlightId == null && insights.length > 1 && !useSidebarHighlightNav;

  const reviewCountNum = useMemo(() => {
    const tabRc = parseWelcomeFiniteCount(
      activeTab?.review_count ?? activeTab?.reviewCount,
    );
    if (isWelcomeHierarchicalTopicTab(activeTab) && tabRc != null) {
      return Math.max(0, Math.round(tabRc));
    }

    const wData = widget?.data && typeof widget.data === "object" ? widget.data : {};
    const nData =
      narrativeData?.data && typeof narrativeData.data === "object"
        ? narrativeData.data
        : narrativeData && typeof narrativeData === "object"
          ? narrativeData
          : {};
    const blob = { ...nData, ...wData };
    const fromScope = resolveWelcomeDisplayReviewCount(blob, widget, {
      tabReviewCount: activeTab?.review_count ?? activeTab?.reviewCount,
      total_reviews: blob?.summary_stats?.total_reviews,
    });
    if (fromScope != null) return fromScope;
    const fromContent = parseReviewCountFromTabContent(activeTab);
    if (fromContent != null) return fromContent;
    return null;
  }, [activeTab, widget, narrativeData]);

  const reviewScopeLabel = useMemo(() => {
    const tabRc = parseWelcomeFiniteCount(
      activeTab?.review_count ?? activeTab?.reviewCount,
    );
    const nf = new Intl.NumberFormat(
      getIntlLocale(i18n.language),
    );
    if (isWelcomeHierarchicalTopicTab(activeTab) && tabRc != null) {
      return t("welcome.hierarchicalTabReviewCount", {
        count: nf.format(Math.max(0, Math.round(tabRc))),
      });
    }

    const wData = widget?.data && typeof widget.data === "object" ? widget.data : {};
    const nData =
      narrativeData?.data && typeof narrativeData.data === "object"
        ? narrativeData.data
        : narrativeData && typeof narrativeData === "object"
          ? narrativeData
          : {};
    const blob = { ...nData, ...wData };
    return formatWelcomeReviewScopeLabel(
      t,
      nf,
      resolveWelcomeReviewScopeFromData(blob, widget),
    );
  }, [activeTab, widget, narrativeData, t, i18n.language]);

  const contextSourceItem = useMemo(
    () =>
      findContextScopeItem({
        activeHighlightId,
        effectiveSingleHighlightItems,
        scopeItems,
        parsed,
      }),
    [activeHighlightId, effectiveSingleHighlightItems, scopeItems, parsed],
  );

  const associatedReviewCount = useMemo(() => {
    if (activeHighlightId != null) {
      if (sliceReviewCount != null && Number.isFinite(Number(sliceReviewCount))) {
        const n = Math.max(0, Math.round(Number(sliceReviewCount)));
        return n > 0 ? n : null;
      }
      return null;
    }
    return reviewCountNum;
  }, [activeHighlightId, reviewCountNum, sliceReviewCount]);

  const showReviewsLink = useMemo(() => {
    if (activeHighlightId == null) return true;
    if (sliceReviewCount === undefined) return true;
    return sliceReviewCount > 0;
  }, [activeHighlightId, sliceReviewCount]);

  useEffect(() => {
    if (!widgetId || activeHighlightId == null || !contextSourceItem) {
      setSliceReviewCount(undefined);
      return undefined;
    }
    let cancelled = false;
    setSliceReviewCount(undefined);
    const payload = {
      ...buildHighlightsReviewsRequestPayload({
        widgetId,
        widget,
        activeTopicKey,
        activeThemeLabel,
        contextSourceItem,
        highlightScoped: activeHighlightId != null,
      }),
      count_only: true,
    };
    fetchWelcomeWidgetMetricReviews(25, payload)
      .then(({ total, reviews }) => {
        if (cancelled) return;
        const n =
          total != null && Number.isFinite(Number(total))
            ? Number(total)
            : Array.isArray(reviews)
              ? reviews.length
              : 0;
        setSliceReviewCount(Math.max(0, Math.round(n)));
      })
      .catch(() => {
        if (!cancelled) setSliceReviewCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [
    widgetId,
    widget,
    activeHighlightId,
    contextSourceItem,
    activeTopicKey,
    activeThemeLabel,
  ]);

  const reviewsLinkLabel = useMemo(() => {
    if (associatedReviewCount != null && associatedReviewCount > 0) {
      return t("welcome.highlightsDashReviewsLink", {
        count: associatedReviewCount,
      });
    }
    return t("welcome.highlightsDashReviewsLinkExplore");
  }, [associatedReviewCount, t]);

  const openReviewsDrawer = async () => {
    if (!widgetId) {
      message.warning(t("welcome.highlightsDashReviewsNoWidgetId"));
      return;
    }
    if (openReviewsDrawerInFlightRef.current) {
      return;
    }
    openReviewsDrawerInFlightRef.current = true;

    const headPart =
      highlightsTabHeadingLabel(
        activeTab,
        widget,
        highlightLabelsResolved,
      ) ?? t("welcome.highlightsDashTopicNameMissing");
    const drawerTitle = [headPart, activeThemeLabel, segmentDisplayLabel(activeSegment)]
      .filter(Boolean)
      .join(" · ");

    flushSync(() => {
      setReviewsDrawerTitle(drawerTitle);
      setReviewsList([]);
      setReviewsLoading(true);
      setReviewsDrawerOpen(true);
    });
    try {
      const payload = buildHighlightsReviewsRequestPayload({
        widgetId,
        widget,
        activeTopicKey,
        activeThemeLabel,
        contextSourceItem,
        highlightScoped: activeHighlightId != null,
      });
      const { reviews } = await fetchWelcomeWidgetMetricReviews(25, payload);
      setReviewsList(reviews);
    } catch {
      setReviewsList([]);
    } finally {
      setReviewsLoading(false);
      openReviewsDrawerInFlightRef.current = false;
    }
  };

  const segmentDisplayLabel = (seg) => {
    if (seg.label) return seg.label;
    if (seg.labelKey) return t(`welcome.${seg.labelKey}`);
    return seg.id;
  };

  const firstInsight = insights[0];
  const criticalBullets =
    firstInsight && Array.isArray(firstInsight.evidence) && firstInsight.evidence.length
      ? firstInsight.evidence
      : insights.length
        ? insights.flatMap((ins) => ins.evidence || []).filter(Boolean).slice(0, 8)
        : [];
  const meaningText =
    firstInsight?.meaning ||
    (insights.map((i) => i.meaning).filter(Boolean)[0] || "");
  const actionText = firstInsight?.action || "";
  const actionSteps = splitActionNumbered(actionText);
  const plainFallback = parsed.mode === "plain" ? parsed.text : "";

  const isAllSegment = !activeSegment || activeSegment.id === "all";
  const segmentHasData = activeSegment && activeSegment.hasData !== false;
  const segmentHasInsightFlag =
    activeSegment && activeSegment.hasInsight !== false;

  const hasRenderableContent =
    insights.length > 0 ||
    (parsed.mode === "plain" && String(plainFallback || "").trim() !== "");

  const themeSliceHasData =
    !isThemeFiltered ||
    (Array.isArray(sliceItemsForTheme) && sliceItemsForTheme.length > 0);

  const sliceHasInsightBlob =
    !isThemeFiltered ||
    (Array.isArray(sliceItemsForTheme) &&
      sliceItemsForTheme.length > 0 &&
      sliceItemsForTheme.some((it) => itemHasInsightBlob(it)));

  const showThemeNoData =
    hasBody && isThemeFiltered && Array.isArray(sliceItemsForTheme) && sliceItemsForTheme.length === 0;

  const showThemeNoTrend =
    hasBody &&
    isThemeFiltered &&
    segmentHasData &&
    Array.isArray(sliceItemsForTheme) &&
    sliceItemsForTheme.length > 0 &&
    !sliceHasInsightBlob;

  const showSegmentNoTrend =
    !isThemeFiltered &&
    hasBody &&
    activeSegment &&
    activeSegment.id !== "all" &&
    segmentHasData &&
    !segmentHasInsightFlag;

  const showInsightBox =
    hasBody &&
    segmentHasData &&
    themeSliceHasData &&
    (needsHighlightPick ||
      (isAllSegment
        ? hasRenderableContent
        : effectiveSingleHighlightItems
          ? hasRenderableContent
          : (isThemeFiltered ? sliceHasInsightBlob : segmentHasInsightFlag) &&
            hasRenderableContent));

  const activeHighlightLabel = useMemo(() => {
    if (activeHighlightId == null) return null;
    const idx = scopeItems.findIndex(
      (it, i) => getHighlightId(it, i) === activeHighlightId,
    );
    if (idx < 0) return null;
    return getHighlightTagLabel(scopeItems[idx], idx);
  }, [activeHighlightId, scopeItems]);

  /** Tek satırda segment+tema birleşik kartlar; ayrı tema şeridi / yatay chip satırı yok. */
  const showUnifiedKesitGrid = hasBody && scopeItems.length >= 1;

  const insightContextTags = useMemo(() => {
    if (!hasBody || showMultiInsightStack || needsHighlightPick) return null;
    return buildItemContextTagFields(contextSourceItem, widget, { withWidgetLine: true });
  }, [hasBody, showMultiInsightStack, needsHighlightPick, contextSourceItem, widget]);

  const renderInsightContextTagBlock = (tagPayload) => {
    if (!tagPayload) return null;
    const thKey =
      tagPayload.th != null && String(tagPayload.th).trim() !== ""
        ? normalizeMatchKey(String(tagPayload.th))
        : null;
    const themeIsActive = Boolean(thKey && activeThemeKey === thKey);
    return (
      <div
        className={dashStyles.aiContextTagBlock}
        aria-label={t("welcome.highlightsDashContextTagRow")}
      >
        <div className={dashStyles.aiContextTagRow} role="list">
          {tagPayload.wLine ? (
            <Tag
              className={dashStyles.aiMetaTag}
              role="listitem"
              title={t("welcome.highlightsDashContextTagWidgetPivotTitle")}
            >
              {tagPayload.wLine}
            </Tag>
          ) : null}
          {tagPayload.showItLine && tagPayload.itLine ? (
            <Tag
              className={dashStyles.aiMetaTag}
              role="listitem"
              title={t("welcome.highlightsDashContextTagItemPivotTitle")}
            >
              {tagPayload.itLine}
            </Tag>
          ) : null}
          {tagPayload.showItemPivotUnset ? (
            <Tag
              className={`${dashStyles.aiMetaTag} ${dashStyles.aiMetaTagEmpty}`}
              role="listitem"
              title={t("welcome.highlightsDashPivotItemEmptyTitle")}
            >
              {t("welcome.highlightsDashPivotItemEmpty")}
            </Tag>
          ) : null}
          {tagPayload.th ? (
            <Tag
              className={[
                dashStyles.aiMetaTag,
                themeIsActive ? dashStyles.aiMetaTagThemeFilterActive : "",
              ]
                .filter(Boolean)
                .join(" ")}
              role="listitem"
              color="blue"
              title={t("welcome.highlightsDashContextTagThemeTitle")}
              onClick={() => {
                if (!thKey) return;
                setActiveThemeKey((k) => (k === thKey ? null : thKey));
              }}
              style={{ cursor: thKey ? "pointer" : "default" }}
            >
              {tagPayload.th}
            </Tag>
          ) : null}
          {tagPayload.showCardL && tagPayload.cardL ? (
            <Tag
              className={dashStyles.aiMetaTag}
              role="listitem"
              color="geekblue"
              title={t("welcome.highlightsDashContextLabel")}
            >
              {tagPayload.cardL}
            </Tag>
          ) : null}
        </div>
      </div>
    );
  };

  const reviewsDrawerNode = (
    <Drawer
      {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
      getContainer={false}
      title={reviewsDrawerTitle || t("welcome.highlightsDashReviewsTitle")}
      extra={
        <button
          type="button"
          className="welcome-reviews-drawer-header-close"
          onClick={handleReviewsDrawerClose}
        >
          {t("common.close", "Close")}
        </button>
      }
      onClose={handleReviewsDrawerClose}
      open={reviewsDrawerOpen}
      width={welcomeReviewsDrawerWidth()}
    >
      <Spin spinning={reviewsLoading}>
        <WelcomeDrawerReviewsList
          allReviews={reviewsList}
          reviewsLoading={reviewsLoading}
          showTotalCount
          exportFileLabel={reviewsDrawerTitle}
        />
      </Spin>
    </Drawer>
  );

  const localReviewsDrawer =
    reviewsDrawerOpen && typeof document !== "undefined"
      ? createPortal(reviewsDrawerNode, document.body)
      : null;

  return (
    <>
    <div className={dashStyles.dashRoot}>
      <aside className={dashStyles.sidebar} aria-label={t("welcome.highlightsDashSidebarLabel")}>
        <div className={dashStyles.sidebarHeader}>
          <StarFilled className={dashStyles.sidebarHeaderIcon} />
          <div>
            <div className={dashStyles.sidebarTitle}>{t("welcome.highlightsDashTitle")}</div>
            <div className={dashStyles.sidebarSubtitle}>{t("welcome.highlightsDashSubtitle")}</div>
          </div>
        </div>
        <nav className={dashStyles.topicNav}>
          {safeTabs.map((tab, idx) => {
            const key = String(tab.id ?? `tab-${idx}`);
            const active = key === String(activeTopicKey);
            const IconComp = TOPIC_ICON_COMPONENTS[idx % TOPIC_ICON_COMPONENTS.length];
            const expandedNav = active && useSidebarHighlightNav;
            const tabHeading = highlightsTabHeadingLabel(
              tab,
              widget,
              highlightLabelsResolved,
            );
            return (
              <div key={key} className={dashStyles.topicGroup}>
                <button
                  type="button"
                  aria-expanded={expandedNav}
                  className={`${dashStyles.topicRow} ${active ? dashStyles.topicRowActive : ""}`}
                  onClick={() => {
                    setActiveTopicKey(key);
                    setActiveSegmentId("all");
                    setActiveThemeKey(null);
                  }}
                >
                  <span className={dashStyles.topicRowIcon} aria-hidden>
                    <IconComp />
                  </span>
                  <span
                    className={[
                      dashStyles.topicRowLabel,
                      tabHeading == null ? dashStyles.topicRowLabelMissing : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {tabHeading ?? t("welcome.highlightsDashTopicNameMissing")}
                  </span>
                  {expandedNav ? (
                    <DownOutlined className={dashStyles.topicRowChevron} aria-hidden />
                  ) : (
                    <RightOutlined className={dashStyles.topicRowChevron} aria-hidden />
                  )}
                </button>
                {expandedNav ? (
                  <div
                    className={dashStyles.topicHighlightNav}
                    role="group"
                    aria-label={t("welcome.highlightsDashSidebarHighlightsAria")}
                  >
                    <div className={dashStyles.topicHighlightNavHint}>
                      {t("welcome.highlightsDashSidebarHighlightsHint")}
                    </div>
                    <ul className={dashStyles.topicHighlightList}>
                      {scopeItems.map((it, hiIdx) => {
                        const hid = getHighlightId(it, hiIdx);
                        const hiActive = activeHighlightId === hid;
                        const badge = hiIdx + 1;
                        const snap = decodeEnt(getHighlightTagLabel(it, hiIdx));
                        const titleFull = decodeEnt(String(it.title || snap)).trim();
                        const rowTheme = extractItemTheme(it);
                        const themeLine = hasThemeInItems
                          ? decodeEnt(
                              rowTheme || t("welcome.highlightsDashThemeUnassigned"),
                            )
                          : "";
                        const titleAttr = hasThemeInItems
                          ? [titleFull, `${t("welcome.highlightsDashAiThemeLabel")}: ${themeLine}`]
                              .filter(Boolean)
                              .join(" — ")
                          : titleFull || undefined;
                        return (
                          <li key={hid} className={dashStyles.topicHighlightItem}>
                            <button
                              type="button"
                              className={[
                                dashStyles.topicHighlightBtn,
                                hiActive ? dashStyles.topicHighlightBtnActive : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              aria-current={hiActive ? "true" : undefined}
                              title={titleAttr || undefined}
                              onClick={() => setActiveHighlightId(hid)}
                            >
                              <span className={dashStyles.topicHighlightBtnInner}>
                                <span className={dashStyles.topicHighlightBadge} aria-hidden>
                                  {badge}
                                </span>
                                <span className={dashStyles.topicHighlightBody}>
                                  <span className={dashStyles.topicHighlightSnap}>{snap}</span>
                                  {hasThemeInItems ? (
                                    <span className={dashStyles.topicHighlightTheme}>
                                      <span className={dashStyles.topicHighlightThemePrefix}>
                                        {t("welcome.highlightsDashAiThemeLabel")}
                                      </span>
                                      : {themeLine}
                                    </span>
                                  ) : null}
                                </span>
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>
      </aside>

      <main className={dashStyles.main}>
        <header className={dashStyles.mainHeader}>
          <h2
            className={[
              dashStyles.reportTitle,
              activeTabHeadingLabel == null ? dashStyles.reportTitleMissing : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            {activeTabHeadingLabel != null ? (
              <>
                {activeTabHeadingLabel}{" "}
                {t("welcome.highlightsDashReportSuffix")}
              </>
            ) : (
              t("welcome.highlightsDashTopicNameMissing")
            )}
          </h2>
          <p className={dashStyles.reportMeta}>
            {reviewScopeLabel ||
              t("welcome.highlightsDashAnalyzedLine", {
                count: reviewCountNum != null ? reviewCountNum : "—",
              })}
          </p>
        </header>

        {!hasBody ? (
          <div className={dashStyles.noDataMain}>{t("welcome.39")}</div>
        ) : (
          <>
            {showAudienceSegmentFilter ? (
              <section
                className={dashStyles.segmentSection}
                aria-label={t("welcome.highlightsDashSegmentFilterAria")}
              >
                <div className={dashStyles.segmentLabel}>
                  {t("welcome.highlightsDashSegmentFilter")}
                </div>
                <div className={dashStyles.chipRow}>
                  {segments.map((seg) => {
                    const selected = seg.id === activeSegmentId;
                    const noData = seg.hasData === false;
                    const withInsight = seg.hasInsight === true;
                    return (
                      <button
                        key={seg.id}
                        type="button"
                        aria-disabled={noData}
                        className={[
                          dashStyles.chip,
                          selected ? dashStyles.chipSelected : "",
                          noData ? dashStyles.chipDisabled : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => {
                          if (noData) {
                            message.warning(t("welcome.highlightsDashSegmentNoData"));
                            return;
                          }
                          setActiveSegmentId(seg.id);
                          setActiveThemeKey(null);
                        }}
                      >
                        {noData ? (
                          <StopOutlined className={dashStyles.chipNoEntry} />
                        ) : null}
                        <span>{segmentDisplayLabel(seg)}</span>
                        {!noData && withInsight ? (
                          <span className={dashStyles.chipSparkle}>✨</span>
                        ) : null}
                        {!noData && !withInsight ? (
                          <span className={dashStyles.chipBan}>🚫</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {showUnifiedKesitGrid ? (
              <section
                className={dashStyles.unifiedKesitSection}
                aria-label={t("welcome.highlightsDashUnifiedKesitSectionAria")}
              >
                <div className={dashStyles.unifiedKesitLabel}>
                  {t("welcome.highlightsDashUnifiedKesitSection")}
                </div>
                <div className={dashStyles.unifiedKesitGrid} role="list">
                  {needsHighlightPick && scopeItems.length > 1 ? (
                    <button
                      type="button"
                      role="listitem"
                      className={[
                        dashStyles.unifiedKesitCard,
                        dashStyles.unifiedKesitCardOverview,
                        activeHighlightId == null ? dashStyles.unifiedKesitCardActive : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      aria-pressed={activeHighlightId == null}
                      onClick={() => setActiveHighlightId(null)}
                    >
                      <span className={dashStyles.unifiedKesitEmoji} aria-hidden>
                        ✨
                      </span>
                      <div className={dashStyles.unifiedKesitPrimary}>
                        {t("welcome.highlightsDashUnifiedOverviewTitle")}
                      </div>
                      <div className={dashStyles.unifiedKesitSecondary}>
                        {t("welcome.highlightsDashUnifiedOverviewHint")}
                      </div>
                    </button>
                  ) : null}
                  {scopeItems.map((it, idx) => {
                    const hid = getHighlightId(it, idx);
                    const kind = inferHighlightCardKind(it);
                    const primary = unifiedCardPrimaryTitle(it, kind);
                    const secondary = unifiedCardSecondaryLine(it, kind);
                    const active = activeHighlightId === hid;
                    const badgeClass =
                      kind === "theme"
                        ? dashStyles.unifiedKesitBadgeTheme
                        : kind === "segment"
                          ? dashStyles.unifiedKesitBadgeSegment
                          : dashStyles.unifiedKesitBadgeBoth;
                    const badgeText =
                      kind === "theme"
                        ? t("welcome.highlightsDashCardBadgeTheme")
                        : kind === "segment"
                          ? t("welcome.highlightsDashCardBadgeSegment")
                          : t("welcome.highlightsDashCardBadgeBoth");
                    const rowTheme = extractItemTheme(it);
                    const titleFull = [
                      String(it.title || primary),
                      hasThemeInItems
                        ? `${t("welcome.highlightsDashAiThemeLabel")}: ${decodeEnt(
                            rowTheme || t("welcome.highlightsDashThemeUnassigned"),
                          )}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" — ");
                    return (
                      <button
                        key={hid}
                        type="button"
                        role="listitem"
                        title={titleFull || undefined}
                        className={[
                          dashStyles.unifiedKesitCard,
                          active ? dashStyles.unifiedKesitCardActive : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-pressed={active}
                        onClick={() => setActiveHighlightId(hid)}
                      >
                        <span className={[dashStyles.unifiedKesitBadge, badgeClass].join(" ")}>
                          {badgeText}
                        </span>
                        <div className={dashStyles.unifiedKesitPrimary}>{primary}</div>
                        {secondary ? (
                          <div className={dashStyles.unifiedKesitSecondary}>{secondary}</div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {hasBody && hasThemeInItems && mixedThemeCoverage ? (
              <div className={dashStyles.themeMixedBanner} role="note">
                {t("welcome.highlightsDashThemeMixedDataBanner")}
              </div>
            ) : null}

            <section
              className={dashStyles.aiBox}
              aria-label={t("welcome.highlightsDashAiBoxAria")}
            >
              <div className={dashStyles.aiBoxHeader}>
                <div className={dashStyles.aiBoxHeaderLeft}>
                  ✨ {t("welcome.highlightsDashAiTitle")}{" "}
                  <span className={dashStyles.aiFilterHint}>
                    ({t("welcome.highlightsDashAiFilterPrefix")}{" "}
                    {[segmentDisplayLabel(activeSegment)]
                      .concat(
                        hasThemeInItems
                          ? [
                              `${t("welcome.highlightsDashAiThemeLabel")}: ${
                                activeThemeLabel ?? t("welcome.highlightsDashThemeAll")
                              }`,
                            ]
                          : [],
                      )
                      .concat(
                        activeHighlightLabel
                          ? [
                              `${t("welcome.highlightsDashAiScopeFinding")}: ${activeHighlightLabel}`,
                            ]
                          : [],
                      )
                      .join(" · ")}
                    )
                  </span>
                </div>
                <button
                  type="button"
                  className={dashStyles.aiReviewsLink}
                  onClick={openReviewsDrawer}
                  disabled={!hasBody || !showReviewsLink}
                >
                  {reviewsLinkLabel}
                </button>
              </div>
              {insightContextTags ? renderInsightContextTagBlock(insightContextTags) : null}
              <div className={dashStyles.aiBoxBody}>
                {showThemeNoData ? (
                  <p className={dashStyles.emptyInsight}>{t("welcome.highlightsDashThemeNoData")}</p>
                ) : showSegmentNoTrend ? (
                  <p className={dashStyles.emptyInsight}>{t("welcome.highlightsDashSegmentNoTrend")}</p>
                ) : showThemeNoTrend ? (
                  <p className={dashStyles.emptyInsight}>{t("welcome.highlightsDashThemeNoTrend")}</p>
                ) : showInsightBox && needsHighlightPick ? (
                  <p className={dashStyles.pickHighlightHint}>
                    {t("welcome.highlightsDashPickHighlightHintUnified")}
                  </p>
                ) : showInsightBox && parsed.mode === "plain" && plainFallback ? (
                  <p className={dashStyles.plainAi}>{plainFallback}</p>
                ) : showInsightBox && showMultiInsightStack && parsed.mode === "structured" ? (
                  <div className={dashStyles.aiInsightStack} role="feed">
                    {insights.map((ins, idx) => {
                      const fromIdx =
                        idx < segmentScopedItems.length
                          ? segmentScopedItems[idx]
                          : null;
                      const fromTitle = findItemForInsightTitle(
                        segmentScopedItems,
                        ins.title,
                      );
                      const item = fromIdx || fromTitle;
                      const blockTags = buildItemContextTagFields(item, widget, {
                        withWidgetLine: idx === 0,
                      });
                      const ev = Array.isArray(ins.evidence) && ins.evidence.length
                        ? ins.evidence
                        : [];
                      const steps = splitActionNumbered(ins.action || "");
                      return (
                        <article
                          key={`ins-${idx}-${String(ins.title || idx)}`}
                          className={dashStyles.aiInsightBlock}
                          aria-label={String(ins.title || `Insight ${idx + 1}`)}
                        >
                          {blockTags ? renderInsightContextTagBlock(blockTags) : null}
                          {String(ins.title || "").trim() ? (
                            <h4 className={dashStyles.aiInsightBlockTitle}>
                              {String(ins.title).replace(/^\d+\)\s*/, "")}
                            </h4>
                          ) : null}
                          <div className={dashStyles.aiSection}>
                            <div className={dashStyles.aiSectionTitle}>
                              🔍 {t("welcome.highlightsDashCriticalTitle")}
                            </div>
                            {ev.length ? (
                              <ul className={dashStyles.aiBullets}>
                                {ev.map((b, i) => (
                                  <li key={`c-${idx}-${i}`}>{b}</li>
                                ))}
                              </ul>
                            ) : (
                              <p className={dashStyles.aiMuted}>
                                {t("welcome.highlightsDashNoEvidence")}
                              </p>
                            )}
                          </div>
                          <div className={dashStyles.aiSection}>
                            <div className={dashStyles.aiSectionTitle}>
                              💡 {t("welcome.highlightsDashMeaningTitle")}
                            </div>
                            <p className={dashStyles.aiPara}>
                              {String(ins.meaning || "").trim() || t("welcome.highlightsDashNoMeaning")}
                            </p>
                          </div>
                          <div className={dashStyles.aiSection}>
                            <div className={dashStyles.aiSectionTitle}>
                              🚀 {t("welcome.highlightsDashActionTitle")}
                            </div>
                            {steps.length ? (
                              <ol className={dashStyles.aiNumbered}>
                                {steps.map((step, i) => (
                                  <li key={`a-${idx}-${i}`}>{step}</li>
                                ))}
                              </ol>
                            ) : (
                              <p className={dashStyles.aiMuted}>
                                {t("welcome.highlightsDashNoAction")}
                              </p>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                ) : showInsightBox ? (
                  <>
                    <div className={dashStyles.aiSection}>
                      <div className={dashStyles.aiSectionTitle}>
                        🔍 {t("welcome.highlightsDashCriticalTitle")}
                      </div>
                      {criticalBullets.length ? (
                        <ul className={dashStyles.aiBullets}>
                          {criticalBullets.map((b, i) => (
                            <li key={`c-${i}`}>{b}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className={dashStyles.aiMuted}>{t("welcome.highlightsDashNoEvidence")}</p>
                      )}
                    </div>
                    <div className={dashStyles.aiSection}>
                      <div className={dashStyles.aiSectionTitle}>
                        💡 {t("welcome.highlightsDashMeaningTitle")}
                      </div>
                      <p className={dashStyles.aiPara}>{meaningText || t("welcome.highlightsDashNoMeaning")}</p>
                    </div>
                    <div className={dashStyles.aiSection}>
                      <div className={dashStyles.aiSectionTitle}>
                        🚀 {t("welcome.highlightsDashActionTitle")}
                      </div>
                      {actionSteps.length ? (
                        <ol className={dashStyles.aiNumbered}>
                          {actionSteps.map((step, i) => (
                            <li key={`a-${i}`}>{step}</li>
                          ))}
                        </ol>
                      ) : (
                        <p className={dashStyles.aiMuted}>{t("welcome.highlightsDashNoAction")}</p>
                      )}
                    </div>
                  </>
                ) : (
                  <p className={dashStyles.emptyInsight}>{t("welcome.highlightsDashNoInsight")}</p>
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
    {localReviewsDrawer}
    </>
  );
}
